import { addStyling } from '@plone/volto/helpers/Extensions/withBlockSchemaEnhancer';
import './components/Styles/global.scss';

import { urlValidator } from '@plone/volto/helpers/FormValidation/validators';
import CatalogImageWidget from './components/Widgets/CatalogImageWidget';

import {
  TalkView,
  PublicationView,
  PeopleView,
  TopicView,
  TopicSubpageView,
  JobView,
  CourseView,
  ProjectView,
  ExternalProjectView,
  ThesisView,
  VideoView,
  EventView,
  CompetitionView,
  RobotView,
} from './components';

import {
  peopleListingTemplate,
  newsListingTemplate,
  publicationListingTemplate,
  publicationYearTabsListingTemplate,
  publicationMainListingTemplate,
  publicationTableListingTemplate,
  jobListingTemplate,
  topicListingTemplate,
  projectListingTemplate,
  externalProjectListingTemplate,
  courseListingTemplate,
  courseSemesterTabsTemplate,
  newsYearTabsTemplate,
  talkYearTabsTemplate,
  videoYearTabsTemplate,
  thesisYearTabsTemplate,
  competitionYearTabsTemplate,
  titleListingTemplate,
  linkListListingTemplate,
  sectionTabsListingTemplate,
  thesisListingTemplate,
  fullwidthListingTemplate,
  videoListingTemplate,
  eventListingTemplate,
  talkListingTemplate,
  competitionListingTemplate,
  robotListingTemplate,
} from './components';

import publicationYearSpec, {
  asPeriodTabsSettings,
} from './components/Listing/Publication/publicationYears';
import { periodTabsAsyncData } from './components/Listing/PeriodTabs/getPeriodTabsAsyncData';
import courseSemesterSpec from './components/Listing/Course/courseSemesters';
import effectiveYearSpec from './components/Listing/PeriodTabs/effectiveYears';
import { PeoplePreviewEdit, PeoplePreviewView } from './components';
import { StatsCounterView, StatsCounterEdit } from './components';

// All your imports required for the config here BEFORE this line
import '@plone/volto/config';

const addBooleanSetting = (schema, name, setting) => {
  schema.properties = schema.properties || {};
  if (!schema.properties[name]) {
    schema.properties[name] = {
      type: 'boolean',
      ...setting,
    };
  }
  const defaultFieldset = schema.fieldsets?.find((f) => f.id === 'default');
  if (defaultFieldset && !defaultFieldset.fields.includes(name)) {
    // Insert right before the 'querystring' field (the query-builder
    // widget) rather than appending at the end, so quick display-mode
    // toggles like "Compact" sit above the query builder instead of below
    // it - editors see them first, without scrolling past the query UI.
    const querystringIndex = defaultFieldset.fields.indexOf('querystring');
    if (querystringIndex === -1) {
      defaultFieldset.fields.push(name);
    } else {
      defaultFieldset.fields.splice(querystringIndex, 0, name);
    }
  }
  return schema;
};

// Same placement logic, for settings that are not booleans - the helper
// takes the whole property definition, so `type` simply overrides.
const addSetting = addBooleanSetting;

const addShowAuthorLinksSetting = (schema) =>
  addBooleanSetting(schema, 'show_author_links', {
    title: 'Link author names',
    description:
      'If disabled, author names are shown as plain text without links to their profile.',
    default: true,
  });

/**
 * Generic "Compact (no images)" toggle, reusable across any listing
 * variation's own settings schema - one function, no per-type duplication.
 * When checked, the variation's template receives `compact={true}` and the
 * card hides its image entirely (not just a smaller image).
 */
const addCompactSetting = (schema) =>
  addBooleanSetting(schema, 'compact', {
    title: 'Compact (no images)',
    description: 'Shows the list without images/thumbnails.',
    default: false,
  });

/**
 * Generic "don't link to the full view" toggle, same idea as `compact`
 * above. News is the reason it takes a default: its cards never linked, and
 * the setting was missing there entirely, so it is offered with the box
 * already ticked - the option appears without any existing block changing.
 */
const addHideLinkSetting = (schema, { default: hidden = false } = {}) =>
  addBooleanSetting(schema, 'hide_link', {
    title: "Don't link to the full view",
    description:
      "Title/image don't link to the full view - e.g. when it has no " +
      'meaningful content of its own, or only an overview is intended.',
    default: hidden,
  });

const addCompactAndHideLinkSettings = (schema) => {
  addCompactSetting(schema);
  addHideLinkSetting(schema);
  return schema;
};

/**
 * Opt-in "Grid layout" toggle: switches a listing from the default
 * full-content-width card (image left, text right, one per row) to
 * the same "image on top, ~330px tile, responsive multi-column grid"
 * look already used by News and People - without touching those two
 * or changing anything for callers that don't set it. Not offered on
 * Publications: its cards already carry a lot of text (authors, venue,
 * links) and often long titles, which doesn't fit a narrow grid tile.
 */
const addGridLayoutSetting = (schema) =>
  addBooleanSetting(schema, 'grid_layout', {
    title: 'Grid layout (3 per row)',
    description:
      'Shows the list as a responsive grid of fixed-width cards (image ' +
      'on top), like News or People, instead of one full-width row per item.',
    default: false,
  });

const addPublicationTabListingSettings = (schema) => {
  addShowAuthorLinksSetting(schema);
  addBooleanSetting(schema, 'show_year_headings', {
    title: 'Show year above each publication block',
    description: 'If disabled, results are shown without year headings.',
    default: true,
  });
  addBooleanSetting(schema, 'show_bottom_navigation', {
    title: 'Show bottom navigation bar',
    description: 'If disabled, only the top sticky navigation is shown.',
    default: true,
  });
  return schema;
};

const addPublicationTableSettings = (schema) => {
  addShowAuthorLinksSetting(schema);
  addBooleanSetting(schema, 'show_venue', {
    title: 'Show "Published in" column',
    description:
      'The venue is the widest column - turn it off for a compact ' +
      'year/authors/title/links table.',
    default: true,
  });
  return schema;
};

/**
 * Grid block: "Show full image".
 *
 * Volto's grid is a tile raster - every image is forced to 16:9 with
 * `object-fit: cover`, which is right for teasers and wrong for a logo, a
 * badge or a diagram, where the cropped-off part is the point. With the
 * switch on, the images keep their own proportions and sit on a common
 * baseline, so a row of differently shaped pictures still reads as one row.
 *
 * For photographs the columns block is the better container: it leaves the
 * image block alone entirely, including its S/M/L setting. This switch is for
 * the case where the grid's uniform row is what you want and the pictures
 * must not be cut.
 */
const addGridShowFullImageSetting = ({ schema, intl }) => {
  addStyling({ schema, intl });
  const styles = schema.properties.styles.schema;
  if (!styles.properties.showFullImage) {
    styles.fieldsets[0].fields = [
      'showFullImage',
      ...styles.fieldsets[0].fields,
    ];
    styles.properties.showFullImage = {
      title: 'Show full image',
      description:
        'Show the images whole and at their own proportions instead of ' +
        'cropping them to the grid ratio - for logos, badges and diagrams.',
      type: 'boolean',
    };
  }
  return schema;
};

/**
 * Grid images: where the crop sits, per image.
 *
 * Volto already reads `--grid-images-object-position` (default `top left`) in
 * its own grid stylesheet, so this field needs no CSS of its own: a style key
 * beginning with `--` is written out as an inline custom property on the
 * block, and the rule picks it up from there.
 *
 * Deliberately a plain field with no detection behind it. The lead image of a
 * content item has its focal point worked out automatically because there are
 * hundreds of them; a picture placed in a page by hand is placed by hand.
 *
 * The enhancer has to be composed with Volto's own, which removes `align` and
 * `size` inside a grid - the grid keeps its own blocksConfig, so replacing it
 * outright would put those two back.
 */
const addGridImageObjectPositionSetting = (voltoEnhancer) => (args) => {
  const schema = voltoEnhancer ? voltoEnhancer(args) : args.schema;
  addStyling({ schema, intl: args.intl });
  const styles = schema.properties.styles.schema;
  const key = '--grid-images-object-position';
  if (!styles.properties[key]) {
    styles.fieldsets[0].fields = [key, ...styles.fieldsets[0].fields];
    styles.properties[key] = {
      title: 'Crop position',
      description:
        'Where the visible part sits when the image is cropped to the grid ' +
        "ratio. Empty means the top left corner, which is Volto's default. " +
        'Examples: "50% 20%" for horizontally centred and a fifth down, ' +
        '"center", "bottom".',
      type: 'string',
    };
  }
  return schema;
};

const addPeriodTabsSettings = (schema, { pagesHint } = {}) => {
  addSetting(schema, 'tab_count', {
    type: 'number',
    title: 'Periods shown by default',
    description:
      'How many of the most recent periods with results are listed before a ' +
      'visitor picks a single one from the bar.',
    default: 2,
  });
  addSetting(schema, 'tab_pages_base', {
    type: 'string',
    widget: 'url',
    title: 'Period pages live under',
    description:
      pagesHint ||
      'Set this only when each period has its own page. The tabs then link ' +
        'to those pages instead of narrowing the list here.',
  });
  addSetting(schema, 'trailing_link_label', {
    type: 'string',
    title: 'Trailing button: label',
    description: 'Optional last button in the bar, after the periods.',
  });
  addSetting(schema, 'trailing_link_url', {
    type: 'string',
    widget: 'url',
    title: 'Trailing button: target',
    description: 'Leave empty and the button is left out.',
  });
  return schema;
};

const addPublicationYearTabsSettings = (schema) => {
  addShowAuthorLinksSetting(schema);
  addSetting(schema, 'year_tabs_count', {
    type: 'number',
    title: 'Years shown by default',
    description:
      'How many of the most recent years with results are listed before a ' +
      'visitor picks a single year from the bar.',
    default: 2,
  });
  addSetting(schema, 'year_pages_base', {
    type: 'string',
    widget: 'url',
    title: 'Year pages live under',
    description:
      'Set this when each year has its own page, e.g. /publications. The ' +
      'tabs then link to <base>/<year> instead of narrowing the list on ' +
      'this page, and the counts behind them are taken across <base>, so a ' +
      'per-year page still offers the whole bar. Leave empty and one page ' +
      'holds every year.',
  });
  addSetting(schema, 'older_link_label', {
    type: 'string',
    title: 'Older-publications button: label',
    description:
      'Last button in the year bar. Defaults to "Older Publications". The ' +
      "years before the query's range are not tabs - they belong to the " +
      'page this button leads to, which can carry a block of its own.',
  });
  addSetting(schema, 'older_link_url', {
    type: 'string',
    widget: 'url',
    title: 'Older-publications button: target',
    description:
      'Where that button goes, e.g. /publications/legacy. Leave empty and ' +
      'the button is left out.',
  });
  return schema;
};

const addPublicationYearNavigationSetting = (schema) =>
  addBooleanSetting(schema, 'show_year_navigation', {
    title: 'Show year navigation bar',
    description:
      'Shows the same Recent/year/Older Publications navigation as the ' +
      'main Publications page - enable on a per-year page (e.g. ' +
      '/publications/2026) so visitors can jump straight to another year.',
    default: true,
  });

/**
 * Let URL fields hold a site-relative path.
 *
 * The link fields on Publication and Thesis (Local PDF File, File-Link, the
 * pre-print and publication links, ...) point at files uploaded to this site,
 * and that is how the site refers to them: `/papers/IROS_2023_Taouil.pdf`,
 * not an absolute URL with a hostname baked in. 363 of the 400 publications
 * hold their PDF that way.
 *
 * Volto is of two minds about it. `UrlWidget` treats a leading slash as an
 * internal URL and is perfectly happy; the form validator behind it runs a
 * regex that insists on a domain, so saving any of those items failed with
 * "Input must be valid url" for a value the item already held and the widget
 * had just accepted. The backend takes the same view as the widget - see
 * academic.types' SiteURI field.
 *
 * So the site-relative case is answered here and everything else is handed to
 * Volto's own validator unchanged.
 */
const siteRelativeUrlValidator = (args) =>
  typeof args.value === 'string' && /^\/(?!\/)/.test(args.value)
    ? null
    : urlValidator(args);

export default function applyConfig(config) {
  config.registerUtility({
    name: 'url',
    type: 'validator',
    dependencies: { widget: 'url' },
    method: siteRelativeUrlValidator,
  });

  config.blocks.blocksConfig.gridBlock.schemaEnhancer =
    addGridShowFullImageSetting;
  // The grid keeps a deep copy of blocksConfig made at install time, so the
  // image block inside it has to be reached there, not at the top level.
  config.blocks.blocksConfig.gridBlock.blocksConfig.image.schemaEnhancer =
    addGridImageObjectPositionSetting(
      config.blocks.blocksConfig.gridBlock.blocksConfig.image.schemaEnhancer,
    );

  config.blocks.requiredBlocks = [];
  config.blocks.initialBlocks = [];

  // Backend NamedBlobImage fields (Lead Image, Teaser Images, ...) opt into
  // this via directives.widget(..., frontendOptions={"widget": "academic-image"})
  // - see academic.types' schema_mixins.py / author.py / sponsor.py.
  config.widgets.widget['academic-image'] = CatalogImageWidget;

  config.settings = {
    ...config.settings,
    isMultilingual: false,
    supportedLanguages: ['en'],
    defaultLanguage: 'en',
    // File links (papers, thesis/course materials, ...) should open in the
    // browser tab - not force a save-as download - wherever the browser can
    // actually render them (PDFs and images; anything else still downloads,
    // see plone.namedfile's @@display-file allowlist).
    downloadableObjects: [],
    viewableInBrowserObjects: ['File'],
  };

  config.views = {
    ...config.views,
    contentTypesViews: {
      ...config.views.contentTypesViews,
      people: PeopleView,
      talk: TalkView,
      publication: PublicationView,
      topic: TopicView,
      job: JobView,
      course: CourseView,
      project: ProjectView,
      thesis: ThesisView,
      video: VideoView,
      event: EventView,
      competition: CompetitionView,
      robot: RobotView,
      external_project: ExternalProjectView,
    },
    // Selected per-object via the `layout` field (not tied to a portal_type)
    // - e.g. a plain Document used as a Topic's "Publications"/"Downloads"
    // sub-page opts into this by having layout="topic_subpage_view".
    layoutViews: {
      ...config.views.layoutViews,
      topic_subpage_view: TopicSubpageView,
    },
  };

  // Registration order == dropdown order (Volto doesn't sort this itself,
  // verified against core - see the "Listing view variations" investigation
  // in .claude/claude.md). Kept alphabetical by title on purpose, so editors
  // can find a variation without needing to know the site's nav order, and
  // so a newly added variation always slots in predictably. Re-alphabetize
  // when adding/renaming a variation.

  config.blocks.blocksConfig.listing.variations.push({
    id: 'titleListing',
    title: 'Button List',
    template: titleListingTemplate,
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'competitionsListing',
    title: 'Competitions Listing',
    template: competitionListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'coursesListing',
    title: 'Courses Listing',
    template: courseListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'eventsListing',
    title: 'Events Listing',
    template: eventListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'externalProjectsListing',
    title: 'External Projects Listing',
    template: externalProjectListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'fullwidthListing',
    title: 'Fullwidth Listing (Title + Description)',
    template: fullwidthListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'jobsListing',
    title: 'Jobs Listing',
    template: jobListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'linkListListing',
    title: 'Link List',
    template: linkListListingTemplate,
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'newsListing',
    title: 'News Listing',
    template: newsListingTemplate,
    schemaEnhancer: ({ schema }) => {
      addCompactSetting(schema);
      return addHideLinkSetting(schema, { default: true });
    },
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'peopleListing',
    title: 'People Listing',
    template: peopleListingTemplate,
    schemaEnhancer: ({ schema }) => addCompactAndHideLinkSettings(schema),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'projectsListing',
    title: 'Projects Listing',
    template: projectListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'publicationsListing',
    title: 'Publications Listing',
    template: publicationListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addCompactAndHideLinkSettings(
        addPublicationYearNavigationSetting(addShowAuthorLinksSetting(schema)),
      ),
  });

  [
    [
      'newsYearTabs',
      'News Tabs (Years from the Query)',
      newsYearTabsTemplate,
      (schema) => addCompactSetting(addPeriodTabsSettings(schema)),
    ],
    [
      'talksYearTabs',
      'Talks Tabs (Years from the Query)',
      talkYearTabsTemplate,
      (schema) =>
        addGridLayoutSetting(
          addCompactAndHideLinkSettings(addPeriodTabsSettings(schema)),
        ),
    ],
    [
      'videosYearTabs',
      'Videos Tabs (Years from the Query)',
      videoYearTabsTemplate,
      (schema) =>
        addGridLayoutSetting(
          addCompactAndHideLinkSettings(addPeriodTabsSettings(schema)),
        ),
    ],
    [
      'thesisYearTabs',
      'Thesis Tabs (Years from the Query)',
      thesisYearTabsTemplate,
      (schema) => addCompactAndHideLinkSettings(addPeriodTabsSettings(schema)),
    ],
    [
      'competitionsYearTabs',
      'Competitions Tabs (Years from the Query)',
      competitionYearTabsTemplate,
      (schema) =>
        addGridLayoutSetting(
          addCompactAndHideLinkSettings(addPeriodTabsSettings(schema)),
        ),
    ],
  ].forEach(([id, title, template, enhancer]) => {
    config.blocks.blocksConfig.listing.variations.push({
      id,
      title,
      template,
      schemaEnhancer: ({ schema }) => enhancer(schema),
    });
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'coursesSemesterTabs',
    title: 'Courses Tabs (Semesters from the Query)',
    template: courseSemesterTabsTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(
        addCompactAndHideLinkSettings(addPeriodTabsSettings(schema)),
      ),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'publicationsYearTabs',
    title: 'Publications Tabs (Years from the Query)',
    template: publicationYearTabsListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addCompactAndHideLinkSettings(addPublicationYearTabsSettings(schema)),
  });

  // Server-side loading for the period-tabs variations. The listing block has
  // one getAsyncData, so each variation's own queries are chained onto
  // Volto's rather than replacing it: the block's plain query still has to
  // run, because ListingBody only renders a variation template once that
  // query has returned something.
  const publicationYearsAsyncData = periodTabsAsyncData(publicationYearSpec);
  const bySpec = {
    publicationsYearTabs: (p) =>
      publicationYearsAsyncData({ ...p, data: asPeriodTabsSettings(p.data) }),
    coursesSemesterTabs: periodTabsAsyncData(courseSemesterSpec),
    newsYearTabs: periodTabsAsyncData(effectiveYearSpec),
    talksYearTabs: periodTabsAsyncData(effectiveYearSpec),
    videosYearTabs: periodTabsAsyncData(effectiveYearSpec),
    thesisYearTabs: periodTabsAsyncData(effectiveYearSpec),
    competitionsYearTabs: periodTabsAsyncData(effectiveYearSpec),
  };
  config.blocks.blocksConfig.listing.getAsyncData = ((base) => (p) => {
    const promises = base ? base(p) : [];
    const own = bySpec[p?.data?.variation];
    return own ? [...promises, ...own(p)] : promises;
  })(config.blocks.blocksConfig.listing.getAsyncData);

  config.blocks.blocksConfig.listing.variations.push({
    id: 'publicationsMain',
    title: 'Publications Tabs (Static Year Navigation)',
    template: publicationMainListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addCompactAndHideLinkSettings(addPublicationTabListingSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'publicationsTable',
    title: 'Publications Table (striped)',
    template: publicationTableListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addHideLinkSetting(addPublicationTableSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'robotsListing',
    title: 'Robots Listing',
    template: robotListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'sectionTabsListing',
    title: 'Section Tabs',
    template: sectionTabsListingTemplate,
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'talksListing',
    title: 'Talks Listing',
    template: talkListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'thesisListing',
    title: 'Thesis Listing',
    template: thesisListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'topicsListing',
    title: 'Topics Listing',
    template: topicListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.listing.variations.push({
    id: 'videosListing',
    title: 'Videos Listing',
    template: videoListingTemplate,
    schemaEnhancer: ({ schema }) =>
      addGridLayoutSetting(addCompactAndHideLinkSettings(schema)),
  });

  config.blocks.blocksConfig.people_preview = {
    id: 'people_preview',
    title: 'People Preview',
    icon: 'user',
    group: 'common',
    view: PeoplePreviewView,
    edit: PeoplePreviewEdit,
    restricted: false,
    mostUsed: false,
    security: {
      add: 'View',
    },
  };

  config.blocks.blocksConfig.stats_counter = {
    id: 'stats_counter',
    title: 'Stats Counter',
    icon: 'chart bar',
    group: 'common',
    view: StatsCounterView,
    edit: StatsCounterEdit,
    restricted: false,
    mostUsed: false,
    security: {
      add: 'View',
    },
  };

  return config;
}
